import { useQuery } from "@apollo/client";
import {
  GET_PLATFORM_METRICS,
  GET_PLATFORM_CASH_FLOW,
  GET_PLATFORM_TOP_CATEGORIES,
  GET_PLATFORM_TOP_COMPANIES,
} from "graphql/platform";
import type {
  CashFlow,
  PlatformCompany,
  PlatformMetrics,
  PlatformTopCategory,
} from "components/platform/dashboard";

interface TopCompanyNode {
  id: string;
  name: string;
  totalSpend: number;
  changePercent: number | null;
}

export function usePlatformDashboard() {
  const {
    data: metricsData,
    loading: metricsLoading,
    error: metricsError,
    refetch: refetchMetrics,
  } = useQuery<{ platformMetrics: PlatformMetrics }>(GET_PLATFORM_METRICS);

  const {
    data: cashFlowData,
    loading: cashFlowLoading,
    error: cashFlowError,
    refetch: refetchCashFlow,
  } = useQuery<{ platformCashFlow: CashFlow[] }>(GET_PLATFORM_CASH_FLOW);

  const {
    data: topCategoriesData,
    loading: topCategoriesLoading,
    error: topCategoriesError,
    refetch: refetchTopCategories,
  } = useQuery<{ platformTopCategories: PlatformTopCategory[] }>(
    GET_PLATFORM_TOP_CATEGORIES,
  );

  const {
    data: topCompaniesData,
    loading: topCompaniesLoading,
    error: topCompaniesError,
    refetch: refetchTopCompanies,
  } = useQuery<{ platformTopCompanies: TopCompanyNode[] }>(
    GET_PLATFORM_TOP_COMPANIES,
  );

  const companies: PlatformCompany[] = (
    topCompaniesData?.platformTopCompanies ?? []
  ).map((company) => ({
    id: company.id,
    companyName: company.name,
    totalSpend: company.totalSpend,
    changePercent: company.changePercent,
  }));

  return {
    metrics: metricsData?.platformMetrics,
    cashFlow: cashFlowData?.platformCashFlow ?? [],
    companies,
    topCategories: topCategoriesData?.platformTopCategories ?? [],
    loading:
      metricsLoading ||
      cashFlowLoading ||
      topCategoriesLoading ||
      topCompaniesLoading,
    error:
      metricsError ?? cashFlowError ?? topCategoriesError ?? topCompaniesError,
    metricsLoading,
    cashFlowLoading,
    topCategoriesLoading,
    topCompaniesLoading,
    refetch: () => {
      refetchMetrics();
      refetchCashFlow();
      refetchTopCategories();
      refetchTopCompanies();
    },
  };
}
